import { useQuery } from '@tanstack/react-query';

import {
	CalculateTotalsResponse,
	callCalculateTotalsAPI,
	prepareCalculateTotalsRequest,
} from '../services/calculateTotals';
import { CustomFeesMap } from '../OrderSummary/types';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { useGetLastEvent } from './useGetLastEvent';

/**
 * Centralized totals for the Box Office. Sends current ticket selection (and the
 * applied promo `code`, if any) to the backend and returns subtotal/fees/taxes/
 * customFees/total. The backend nets the discount into subtotal and total
 * (calculate_totals.go), so passing the code keeps the charged total in sync with
 * the discounted ticket rows that redux already mutates.
 */
export const useCalculateCost = (promoCode?: string) => {
	const { event, isMultiDay } = useGetLastEvent();
	const { multiday } = useAppSelector(({ lastMultiDay }: RootState) => lastMultiDay);

	const ticketQuantitiesKey = event?.ticketTypes.map((t) => `${t.ID}:${t.quantity}`).join(',') || '';
	const multidayQuantitiesKey =
		multiday?.map((day) => day.ticketTypes.map((t) => `${t.ID}:${t.quantity}`).join(',')).join('|') || '';

	const request = event ? prepareCalculateTotalsRequest(event, multiday || null, isMultiDay, promoCode) : null;

	const {
		data: totals,
		refetch: refetchTotals,
		isFetching: isFetchingTotals,
	} = useQuery<CalculateTotalsResponse | null>(
		['boxOffice-calculateTotals', event?.id, ticketQuantitiesKey, multidayQuantitiesKey, promoCode || ''],
		async () => (request ? callCalculateTotalsAPI(request) : null),
		{
			enabled: !!event && !!request,
			staleTime: 0,
			cacheTime: 0,
			// Keep the prior totals on screen while a new selection refetches, instead of dropping to
			// 0 — avoids a $0 flicker (and a momentary "free ticket" mislabel) in the Order Summary.
			keepPreviousData: true,
		}
	);

	const subtotal = totals?.subtotal ?? 0;
	const myTaxes = totals?.taxes ?? 0;
	const myFees = totals?.fees ?? 0;
	const totalCost = totals?.total ?? 0;

	const customFees: CustomFeesMap | undefined = totals?.details
		? totals.details.reduce<CustomFeesMap>((acc, detail) => {
				if (detail.custom_fees && detail.custom_fees > 0) acc[detail.ticket_type_id] = detail.custom_fees;
				return acc;
		  }, {})
		: undefined;

	return {
		subtotal,
		myTaxes,
		myFees,
		customFees,
		totalCost,
		refetchTotals,
		isFetchingTotals,
	};
};
